import React from 'react';
import i18n from 'i18next';
import PropTypes from 'prop-types';

import { Avatar } from 'uikit/avatars';
import { Drawer } from 'uikit/layouts';
import { Skeleton } from 'uikit/loaders';
import { Typo } from 'uikit/content';

import styles from '../styles/enrollmentsDrawer.module.scss';
import useEnrollmentDetails from '../hooks/useEnrollmentDetails';

import EnrollmentStatusText from './enrollmentStatusText';

const EnrollmentsDrawer = ({
  enrollmentId = null,
  isEnrollmentsDrawerActive,
  onToggleEnrollmentsDrawer,
  triggerRef = null,
}) => {
  const { enrollmentDetails } = useEnrollmentDetails({
    enrollmentId,
    isEnabled: Boolean(enrollmentId) && isEnrollmentsDrawerActive,
  });

  const isLoading = !enrollmentDetails.isSuccess;
  const userEnrollment = enrollmentDetails.data || {};
  const user = userEnrollment.user || {};

  const getDateString = (date) =>
    date
      ? new Date(date).toLocaleString(document.documentElement.lang || 'en', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        })
      : '';

  return (
    <Drawer
      aria-labelledby="enrollments-show-drawer-heading"
      className={styles.element}
      hasCloseButton
      id="enrollments-show-drawer"
      isActive={isEnrollmentsDrawerActive}
      isSocial={false}
      onCloseButtonClick={onToggleEnrollmentsDrawer}
      placement="right"
      position="fixed"
      triggerRef={triggerRef}
    >
      <div className={styles.content}>
        <div className={styles.header}>
          {isLoading ? (
            <Skeleton isCircle width={42} height={42} />
          ) : (
            <Avatar avatar={user.avatar_url} name={user.full_name} size="large" />
          )}
          <div>
            {isLoading ? (
              <Skeleton width={200} height={50} />
            ) : (
              <>
                <Typo element="h2" variant="d03" weight="medium" id="enrollments-show-drawer-heading">
                  {`${user.last_name}, ${user.first_name}`}
                </Typo>
                <Typo variant="b01">{user.code}</Typo>
              </>
            )}
          </div>
        </div>
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            {isLoading ? (
              <Skeleton width={100} height={24} />
            ) : (
              <Typo element="h3" variant="d01" weight="medium">
                {i18n.t('user-details')}
              </Typo>
            )}
          </div>
          <div className={styles.rows}>
            {isLoading ? (
              <Skeleton width="100%" height={24} count={5} />
            ) : (
              <>
                <div className={styles.row}>
                  <Typo variant="b01">{i18n.t('user-authority')}</Typo>
                  <Typo variant="b01">{i18n.t(user.authority)}</Typo>
                </div>
                <div className={styles.row}>
                  <Typo variant="b01">{i18n.t('hired')}</Typo>
                  <Typo variant="b01">{getDateString(user.hired_on)}</Typo>
                </div>
                <div className={styles.row}>
                  <Typo variant="b01">{i18n.t('manager')}</Typo>
                  <Typo variant="b01">{user.manager}</Typo>
                </div>
                {Object.keys(user.custom_fields || {}).map((field, i) => (
                  <div className={styles.row} key={i}>
                    <Typo variant="b01">{field}</Typo>
                    <Typo variant="b01">{user.custom_fields[field]}</Typo>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            {isLoading ? (
              <Skeleton width={100} height={24} />
            ) : (
              <Typo element="h3" variant="d01" weight="medium">
                {i18n.t('enrollment-details')}
              </Typo>
            )}
          </div>
          <div className={styles.rows}>
            {isLoading ? (
              <Skeleton width="100%" height={24} count={5} />
            ) : (
              <>
                <div className={styles.row}>
                  <Typo variant="b01">{i18n.t('code')}</Typo>
                  <Typo variant="b01">{userEnrollment.code}</Typo>
                </div>
                <div className={styles.row}>
                  <Typo variant="b01">{i18n.t('enrolled')}</Typo>
                  <Typo variant="b01">{getDateString(userEnrollment.enrolled_on)}</Typo>
                </div>
                <div className={styles.row}>
                  <Typo variant="b01">{i18n.t('due')}</Typo>
                  <Typo variant="b01">
                    {getDateString(userEnrollment.due_on, { timeZone: 'UTC' })}
                  </Typo>
                </div>
                <div className={styles.row}>
                  <Typo variant="b01">{i18n.t('started')}</Typo>
                  <Typo variant="b01">{getDateString(userEnrollment.started_on)}</Typo>
                </div>
                <div className={styles.row}>
                  <Typo variant="b01">{i18n.t('completed')}</Typo>
                  <Typo variant="b01">{getDateString(userEnrollment.completed_on)}</Typo>
                </div>
                <div className={styles.row}>
                  <Typo variant="b01">{i18n.t('type')}</Typo>
                  <Typo variant="b01">{i18n.t(userEnrollment.relationship_type)}</Typo>
                </div>
                <div className={styles.row}>
                  <Typo variant="b01">{i18n.t('status')}</Typo>
                  <Typo element="div" variant="b01">
                    <EnrollmentStatusText item={userEnrollment} percentageTypoVariant="b01" />
                  </Typo>
                </div>
                <div className={styles.row}>
                  <Typo variant="b01">
                    {userEnrollment.relationship_type === 'required'
                      ? i18n.t('required-by')
                      : i18n.t('recommended-by')}
                  </Typo>
                  <Typo variant="b01">{userEnrollment.recommended_by}</Typo>
                </div>
                <div className={styles.row}>
                  <Typo variant="b01">{i18n.t('reason')}</Typo>
                  <Typo variant="b01">{userEnrollment.relationship_reason}</Typo>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </Drawer>
  );
};

EnrollmentsDrawer.propTypes = {
  enrollmentId: PropTypes.number,
  isEnrollmentsDrawerActive: PropTypes.bool.isRequired,
  onToggleEnrollmentsDrawer: PropTypes.func.isRequired,
  triggerRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
};

export default EnrollmentsDrawer;
